import type { JSX } from 'react';
import { FormGroup } from 'react-bootstrap';
import Select, { OnChangeValue, OptionProps, Props, components } from 'react-select';

import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import styles from 'Components/FormField/FormField.module.css';
import { FormLabel } from 'Components/FormField/FormLabel/FormLabel';
import { SELECT_MENU_Z_INDEX } from 'Components/FormField/SelectComponentsZIndex';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { getUserNameFromUser, sortFormattedUsers } from 'Helpers/UserUtils';
import { ColorTheme } from 'Models/Types/GlobalType';
import { Status, UserResponse } from 'Models/User';

import { UserOption, UserOptionProps } from './UserOption/UserOption';

interface FormFieldUserMultiSelectProps {
    formFieldId: string;
    formFieldLabel?: string;
    accessibilityLabel?: string;
    required?: boolean;
    tooltip?: string;
    onUsersSelected?: (value: UserResponse[] | undefined, formFieldId: string) => void;
    selectedUsers?: UserResponse[];
    disabled?: boolean;
    includeInactive?: boolean;
    /**
     * If defined, the component will only show users with the specified roles. If not defined, all users will be shown.
     */
    rolesRestriction?: Role[];

    /**
     * The type of background that the component will be displayed against. If not supplied, `'light'` will be used.
     */
    colorTheme?: ColorTheme;
}

class UserResponseOption {
    value: string;
    label: string;
    user: UserResponse;

    constructor(user: UserResponse) {
        this.value = user.cognito_subject;
        this.label = getUserNameFromUser(user);
        this.user = user;
    }
}

const userOption = (props: OptionProps<UserResponseOption, true>): JSX.Element => {
    const userOptionProps: UserOptionProps = {
        user: props.data.user,
    };
    return (
        <components.Option {...props}>
            <UserOption {...userOptionProps} />
        </components.Option>
    );
};

// TODO: can this be combined with FormFieldMultiOptionSelect? They share quite a bit of code.
// TODO: This is inconsistent with `FormFieldMultiOptionSelectProps` in at least two ways:
// - This component does not require (or even allow) an `accessibilityLabel`.
// - This component is expected to be controlled, whereas FormFieldMultiOptionSelect is expected to be uncontrolled.
export const FormFieldUserMultiSelect = (props: FormFieldUserMultiSelectProps) => {
    const colorTheme = props.colorTheme ?? 'light';

    const cachedData = useCachedData();
    const { rolesRestriction } = props;

    const mapUserToUserOption = (user: UserResponse): UserResponseOption => new UserResponseOption(user);

    const filterAndFormatUsers = (): UserResponseOption[] => {
        let users = [...cachedData.users];

        if (rolesRestriction !== undefined) {
            users = users.filter((user) => userHasAuthorizedRole(user.roles, rolesRestriction));
        }

        if (!props.includeInactive) {
            users = users.filter((user) => user.status === Status.ACTIVE);
        }

        return sortFormattedUsers(users).map(mapUserToUserOption);
    };

    const handleChange = (value: OnChangeValue<UserResponseOption, true>): void => {
        if (props.onUsersSelected) {
            if (value && value instanceof Array) {
                props.onUsersSelected(
                    value.map((userOption) => userOption.user),
                    props.formFieldId
                );
            } else {
                props.onUsersSelected(undefined, props.formFieldId);
            }
        }
    };

    const selectProps: Props<UserResponseOption, true> = {
        options: filterAndFormatUsers(),
        onChange: handleChange,
        value: props.selectedUsers?.map(mapUserToUserOption),
        isDisabled: props.disabled,
        isMulti: true,
        inputId: props.formFieldId,
    };

    return (
        <FormGroup controlId={props.formFieldId} className={styles.formField}>
            {props.formFieldLabel && (
                <FormLabel color={colorTheme === 'light' ? undefined : 'white'} required={props.required}>
                    {props.formFieldLabel}
                </FormLabel>
            )}
            {props.tooltip && <FormFieldTooltip text={props.tooltip} />}
            <Select
                {...selectProps}
                components={{ Option: userOption }}
                theme={(theme) => ({
                    ...theme,
                    borderRadius: 9,
                    colors: {
                        ...theme.colors,
                        primary25: '#05263717',
                        text: 'var(--hps-blue)',
                        primary: colorTheme === 'light' ? 'var(--hps-blue)' : 'transparent',
                    },
                })}
                styles={{
                    ...styles,
                    menu: (base) => ({
                        ...base,
                        zIndex: SELECT_MENU_Z_INDEX,
                    }),
                    control: (base) => ({
                        ...base,
                        borderColor: '#053c591a',
                        color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-white)',
                        backgroundColor: colorTheme === 'light' ? base.backgroundColor : 'var(--hps-blue)',
                    }),
                    multiValue: (styles) => ({
                        ...styles,
                        backgroundColor: colorTheme === 'light' ? styles.backgroundColor : 'var(--hps-white)',
                    }),
                    multiValueRemove: (styles, _) => ({
                        ...styles,
                        color: colorTheme === 'light' ? 'var(--hps-gray)' : 'var(--hps-blue)',
                        ':hover': {
                            color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-gray)',
                        },
                    }),
                    option: (styles, { isSelected }) => ({
                        ...styles,
                        backgroundColor: isSelected ? '#05263717' : '',
                        color: isSelected ? 'var(--hps-blue)' : '',
                    }),
                    input: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-white)',
                    }),
                    placeholder: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? base.color : 'var(--hps-white)',
                    }),
                    dropdownIndicator: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? base.color : 'var(--hps-white)',
                        ':hover': {
                            color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-gray)',
                        },
                    }),
                    clearIndicator: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? base.color : 'var(--hps-white)',
                        ':hover': {
                            color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-gray)',
                        },
                    }),
                }}
            />
        </FormGroup>
    );
};
